import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  Clock,
  Wallet,
  Menu,
  X,
  ArrowBigRightDash,
  ArrowBigLeftDash,
  LogOut,
  ClipboardList,
  CalendarCheck
} from "lucide-react";

const EmployeeCornerSidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState([]); // track all open parent menus
  const navigate = useNavigate();
  const location = useLocation();

  const menus = [
     { name: "Attendance", path: "/EmployeeAttendance", icon: CalendarCheck },
    { name: "My Shift Time", path: "/EmployeeShiftTime", icon: Clock },
    { name: "Leave Approval", path: "/EmployeeLeaveDetails", icon: ClipboardList },
    { name: "My Leave Status", path: "/employeeHome", icon: LayoutDashboard },
    {
      name: "Employee Access",
      icon: Users,
      submenus: [
        { name: "User Profile", path: "/UserProfile", icon: Users },
        // { name: "Holiday Calendar", path: "/EmployeeCalendar", icon: CalendarDays },
        { name: "Leave Application", path: "/EmployeeLeaveApplication", icon: FileText },
        { name: "Employee Pay Slip", path: "/EmployeePayslipView", icon: Wallet },
        { name: "Training Documents", path: "/EmployeeTraining", icon: FileText },
        // { name: "HR Policy", path: "", icon: Clock },
      ],
    },
  ];

  const mobileTabs = [
    { name: "Home", path: "/EmployeeHome", icon: LayoutDashboard },
    { name: "Attendance", path: "/EmployeeAttendance", icon: CalendarCheck },
    { name: "Shift", path: "/EmployeeShiftTime", icon: Clock },
    { name: "Leave", path: "/EmployeeLeaveApplication", icon: FileText },
    { name: "More", action: () => setMobileOpen(true), icon: Menu },
  ];

  const handleLogout = () => {
    localStorage.removeItem("employeeData");
    localStorage.removeItem("token");
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("employeeUser");
    navigate("/");
  };

  // Keep parent menus open if current route is inside
  useEffect(() => {
    const activeParents = menus
      .filter(menu => menu.submenus && menu.submenus.some(sub => location.pathname === sub.path))
      .map(menu => menu.name);

    setOpenMenus(activeParents);
  }, [location.pathname]);

  const toggleMenu = (menuName) => {
    setOpenMenus(prev => {
      if (prev.includes(menuName)) return prev.filter(name => name !== menuName);
      return [...prev, menuName];
    });
  };

  const isMenuActive = (menu) => {
    if (menu.path && location.pathname === menu.path) return true;
    if (menu.submenus) {
      return menu.submenus.some(sub => location.pathname === sub.path);
    }
    return false;
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-cyan-200 bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-700 px-4 py-3 text-white shadow-lg">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-100">Employee</p>
          <h2 className="font-bold text-white">Corner</h2>
        </div>
        <button onClick={() => setMobileOpen(true)}>
          <Menu size={26} className="text-white" />
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 bg-gray-800 text-white transform transition-transform duration-300 ease-in-out z-50
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} 
        md:sticky md:top-0 md:h-screen md:translate-x-0 md:flex md:flex-col
        ${isOpen ? "md:w-56" : "md:w-16"} 
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-1 p-1">
            <button className="hidden md:block text-white" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <ArrowBigLeftDash size={22} /> : <ArrowBigRightDash size={22} />}
            </button>
            <button className="md:hidden text-white ml-auto" onClick={() => setMobileOpen(false)}>
              <X size={28} />
            </button>
          </div>

          {isOpen && <h2 className="hidden md:block text-xl font-bold mb-1 px-4">Employee Corner</h2>}

          <ul className="space-y-2 px-2 flex-1 overflow-y-auto">
            {menus.map((menu, i) => {
              const Icon = menu.icon;
              const active = isMenuActive(menu);
              const isOpenParent = openMenus.includes(menu.name);

              return (
                <li key={i}>
                  <button
                    onClick={() => menu.submenus ? toggleMenu(menu.name) : navigate(menu.path)}
                    className={`flex items-center gap-3 p-2 w-full text-left rounded transition-colors
                    ${active ? "bg-blue-600 text-white" : "hover:bg-gray-700"}`}
                  >
                    <Icon size={20} />
                    {isOpen && <span>{menu.name}</span>}
                  </button>

                  {menu.submenus && isOpenParent && (
                    <ul className="pl-6 space-y-1 mt-1">
                      {menu.submenus.map((sub, j) => (
                        <li key={j}>
                          <NavLink
                            to={sub.path}
                            className={({ isActive }) =>
                              `flex items-center gap-3 p-2 rounded text-sm transition-colors
                              ${isActive ? "bg-blue-600 text-white" : "hover:bg-gray-700"}`
                            }
                          >
                            <sub.icon size={18} />
                            {isOpen && <span>{sub.name}</span>}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="px-2 mb-4">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full bg-red-600 text-white p-2 rounded transition"
            >
              <LogOut size={20} />
              {isOpen && <span>Logout</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        ></div>
      )}

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-cyan-300 bg-gradient-to-r from-slate-950 via-sky-950 to-cyan-950 px-2 pb-[calc(env(safe-area-inset-bottom,0px)+0.5rem)] pt-2 shadow-[0_-12px_30px_rgba(8,47,73,0.28)] md:hidden">
        <div className="grid grid-cols-5 gap-1">
          {mobileTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.path ? location.pathname === tab.path : false;

            if (tab.path) {
              return (
                <NavLink
                  key={tab.name}
                  to={tab.path}
                  className={`flex flex-col items-center justify-center rounded-2xl px-2 py-2 text-[11px] font-semibold transition ${
                    isActive ? "bg-white text-sky-900 shadow-md" : "text-cyan-100"
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.name}</span>
                </NavLink>
              );
            }

            return (
              <button
                key={tab.name}
                onClick={tab.action}
                className="flex flex-col items-center justify-center rounded-2xl px-2 py-2 text-[11px] font-semibold text-cyan-100 transition"
              >
                <Icon size={18} />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default EmployeeCornerSidebar;
